import React, { Fragment, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { ChevronDown, Link2, Lock, Plus } from 'lucide-react';

import Toggle from 'components/common/Toggle';
import UserAvatar from 'components/common/UserAvatar';
import { workStatusUIMap } from '../Dropdowns/StatusDropdown';
import { workTypeUIMap } from '../Dropdowns/TaskTypeDropdown';
import { usePmoNavigation } from 'hooks/usePmoNavigation';
import { ProjectUser } from 'types/project';
import { EpicChild } from 'types/project/epic';
import { WorkItemLinkState } from 'types/project/tasks';
import { toTitleCase } from 'utils/helpers';

const TYPE_LABELS: Record<string, string> = { STORY: 'Story', TASK: 'Task', BUG: 'Bug' };

const SPRINT_STATUS_LABELS = { ACTIVE: 'Active', PLANNING: 'Planned', COMPLETED: 'Completed' } as const;

const SprintPill: React.FC<{ item: EpicChild }> = ({ item }) => {
  if (!item.sprintName || !item.sprintStatus) {
    return <span className="rounded-md bg-primary-subtle px-2 py-0.5 text-xs text-neutral-medium">Backlog</span>;
  }
  const tone =
    item.sprintStatus === 'ACTIVE'
      ? 'bg-[#10B98122] text-[#34D399]'
      : item.sprintStatus === 'COMPLETED'
        ? 'bg-primary-subtle text-neutral-medium'
        : 'bg-primary-subtle text-neutral';
  return (
    <span className={`inline-block max-w-full truncate rounded-md px-2 py-0.5 text-xs ${tone}`}>
      {toTitleCase(item.sprintName)} · {SPRINT_STATUS_LABELS[item.sprintStatus]}
    </span>
  );
};

/** The status as a pill, shared with the link modal. */
export const ChildStatusPill: React.FC<{ item: EpicChild }> = ({ item }) => {
  const status = workStatusUIMap[item.workStatusCategory] ?? workStatusUIMap.TODO;
  return (
    <span className={`inline-flex shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium ${status.bg} ${status.border} ${status.text}`}>
      {item.workStatusName ?? toTitleCase(item.workStatusCategory.replace('_', ' '))}
    </span>
  );
};

const COLUMNS =
  'grid grid-cols-[minmax(110px,0.9fr)_minmax(0,2.4fr)_minmax(60px,0.6fr)_minmax(100px,0.9fr)_minmax(70px,0.6fr)_minmax(140px,1.1fr)] items-center gap-3';

const ChildRow: React.FC<{ item: EpicChild; assignee?: ProjectUser; projectKey: string }> = ({
  item,
  assignee,
  projectKey,
}) => {
  const { buildTaskUrl } = usePmoNavigation();
  const type = workTypeUIMap[item.workTypeCategory];
  const state: WorkItemLinkState | undefined = item.readOnly
    ? {
        readOnlyNote: `Completed in ${toTitleCase(item.sprintName ?? 'a completed sprint')}. This work item is view only.`,
      }
    : undefined;
  const assigneeName = assignee ? `${assignee.firstName} ${assignee.lastName}` : null;

  return (
    <div className={`${COLUMNS} border-t border-primary-subtle px-4 py-3 text-sm`}>
      <span className="flex min-w-0 items-center gap-2">
        {type && <span className={`${type.color} shrink-0`}>{type.icon}</span>}
        <Link
          to={buildTaskUrl(projectKey, item.workItemKey)}
          state={state}
          className="truncate font-medium text-neutral hover:underline"
        >
          {item.workItemKey}
        </Link>
      </span>
      <span className="truncate text-neutral">{item.title}</span>
      <span className="text-neutral-medium">
        {item.workTypeName ?? TYPE_LABELS[item.workTypeCategory] ?? item.workTypeCategory}
      </span>
      <span>
        <ChildStatusPill item={item} />
      </span>
      <span>
        {assigneeName ? (
          <span title={assigneeName} className="inline-flex">
            <UserAvatar name={assigneeName} profileImageURL={assignee?.profileImageURL} className="h-6 w-6 text-[10px]" />
          </span>
        ) : (
          <span className="text-neutral-medium">–</span>
        )}
      </span>
      <span className="flex min-w-0 items-center gap-2">
        <SprintPill item={item} />
        {item.readOnly && <Lock size={14} className="shrink-0 text-neutral-medium" aria-label="View only" />}
      </span>
    </div>
  );
};

interface EpicChildItemsProps {
  /** As returned for the current `hideDone`. */
  items: EpicChild[];
  /** Every child, done ones included. */
  totalCount: number;
  doneCount: number;
  hideDone: boolean;
  onHideDoneChange: (hideDone: boolean) => void;
  isLoading?: boolean;
  members: ProjectUser[];
  projectKey: string;
  onCreateChild: () => void;
  onLinkExisting: () => void;
  readOnly?: boolean;
}

/**
 * An Epic's Stories, Tasks and Bugs with the sprint each is in. Done items are
 * hidden by default; shown, those of completed sprints are view only.
 */
const EpicChildItems: React.FC<EpicChildItemsProps> = ({
  items,
  totalCount,
  doneCount,
  hideDone,
  onHideDoneChange,
  isLoading = false,
  members,
  projectKey,
  onCreateChild,
  onLinkExisting,
  readOnly = false,
}) => {
  const membersByUuid = useMemo(() => new Map(members.map((m) => [m.appUserUuid, m])), [members]);
  const showLockNote = hideDone ? doneCount > 0 : items.some((item) => item.readOnly);

  return (
    <section className="mb-6 rounded-xl border border-primary-subtle bg-primary-light">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-neutral">Child work items</h3>
          <span className="rounded-full bg-primary-subtle px-2 py-0.5 text-[11px] font-medium text-neutral-medium">
            {totalCount}
          </span>
        </div>
        <div className="flex items-center gap-4">
          {doneCount > 0 && <Toggle checked={hideDone} onChange={onHideDoneChange} label="Hide done work items" />}
          {!readOnly && (
            <Menu as="div" className="relative">
              <MenuButton className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold button-active">
                <Plus size={16} /> Add child <ChevronDown size={14} />
              </MenuButton>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <MenuItems
                  anchor="bottom end"
                  className="z-[100] mt-2 w-60 rounded-xl border border-primary-subtle bg-primary-variant py-1 shadow-2xl focus:outline-none"
                >
                  <MenuItem>
                    <button
                      type="button"
                      onClick={onCreateChild}
                      className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-neutral-light data-[focus]:bg-primary-subtle data-[focus]:text-white"
                    >
                      <Plus size={14} /> Create work item
                    </button>
                  </MenuItem>
                  <MenuItem>
                    <button
                      type="button"
                      onClick={onLinkExisting}
                      className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-neutral-light data-[focus]:bg-primary-subtle data-[focus]:text-white"
                    >
                      <Link2 size={14} /> Link existing work items
                    </button>
                  </MenuItem>
                </MenuItems>
              </Transition>
            </Menu>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-3 border-t border-primary-subtle px-4 py-4">
          {[0, 1].map((i) => (
            <span key={i} className="h-4 w-full animate-pulse rounded bg-primary-subtle" />
          ))}
        </div>
      ) : totalCount === 0 ? (
        <p className="border-t border-primary-subtle px-4 py-6 text-center text-sm text-neutral-medium">
          No child work items yet. Add a Story, Task or Bug to plan this Epic.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[720px]">
            <div className={`${COLUMNS} border-t border-primary-subtle px-4 py-2 text-xs font-medium text-neutral-medium`}>
              {['Work ID', 'Title', 'Type', 'Status', 'Assignee', 'Sprint'].map((label) => (
                <span key={label}>{label}</span>
              ))}
            </div>
            {items.map((item) => (
              <ChildRow
                key={item.workItemUuid}
                item={item}
                assignee={item.workItemAssigneeAppUserUuid ? membersByUuid.get(item.workItemAssigneeAppUserUuid) : undefined}
                projectKey={projectKey}
              />
            ))}
            {items.length === 0 && (
              <p className="border-t border-primary-subtle px-4 py-4 text-center text-sm text-neutral-medium">
                All child work items are done.
              </p>
            )}
          </div>
        </div>
      )}

      {!isLoading && ((hideDone && doneCount > 0) || showLockNote) && (
        <div className="flex flex-col gap-2 border-t border-primary-subtle px-4 py-3 text-xs text-neutral-medium">
          {hideDone && doneCount > 0 && (
            <p>
              {doneCount} done work {doneCount === 1 ? 'item' : 'items'} hidden ·{' '}
              <button
                type="button"
                onClick={() => onHideDoneChange(false)}
                className="font-medium text-[#A78BFA] hover:text-[#C4B5FD]"
              >
                Show them
              </button>
            </p>
          )}
          {showLockNote && (
            <p className="flex items-center gap-2">
              <Lock size={12} /> Done work items from completed sprints are read-only here. Find them in Sprint History.
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default EpicChildItems;
